import React, { useState, useEffect } from 'react';
import { createContext, useContext } from 'react';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

// initialize the context with an empty object
const UserContext = createContext({});

export const AuthContext = ({ children }) => {
  // Store the user in a state variable default null
  const [user, setUser] = useState(null);
  useEffect(() => {
    checkUser();
  }, []);

  // (Only once) - when the component mounts, create a listener for any auth events.
  useEffect(() => {
    Hub.listen('auth', () => {
      // Hub listens for auth events that happen.
      // i.e. Sign in, sign out, sign up, etc.
      // Whenever an event gets detected, run the checkUser function
      checkUser();
    });
  }, []);

  async function checkUser() {
    try {
      // Get the current authenticated user
      const currentUser = await getCurrentUser();
      if (currentUser) {
        // Get the auth session to access tokens and groups
        const session = await fetchAuthSession();
        const groups = session.tokens?.accessToken?.payload?.['cognito:groups'] || [];
        
        // Create a user object that includes groups for backward compatibility
        const userWithGroups = {
          ...currentUser,
          signInUserSession: {
            accessToken: {
              payload: {
                'cognito:groups': groups
              }
            }
          }
        };
        
        // set the user in state
        setUser(userWithGroups);
      }
    } 
    // Error occurs if there is no user signed in.
    catch (error) {
      // set the user to undefined if there is no user. 
      setUser(undefined);
    }
  }

  return (
    <UserContext.Provider
      value={{
        user, // the value of the current user
        setUser, // the setState method - allows us to set the current user in state from anywhere in our application
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

// export the hook so we can use it in other components.
export const useUser = () => useContext(UserContext);
